import type { Maybe } from '@adornis/base/utilTypes.js';
import { subscribeByID } from '@adornis/baseql/operations/mongo.js';
import { stateful, useEffect } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { makeRouter } from '@adornis/router/client/make-router.js';
import { addPageHeaderAdditional, removePageHeaderAdditional } from '@martinus/styles/client/PageHeader.js';
import { PageLoader } from '@martinus/styles/client/PageLoader.js';
import { PageTabs } from '@martinus/styles/client/PageTabs.js';
import { html, nothing } from 'lit';
import { SGCalender } from '../../../calender/client/sg-calender.js';
import { MitarbeiterTicket } from '../../../client/components/job-ticket/MitarbeiterTicket.js';
import { ContractsView } from '../../../contracts/client/components/ContractsView.js';
import { ContractsActions } from '../../../contracts/ContractsPolicy.js';
import { employmentPermitListRouter } from '../../../employment-permits/employment-permits-router.js';
import { LAKEmployeeOverview } from '../../../lak/client/LAKEmployeeOverview.js';
import { NoPermission } from '../../../permissions/client/NoPermission.js';
import { type AbacEnabledPage, useAbacEnabledPages } from '../../../permissions/client/useAbacEnabledPages.js';
import { Person } from '../../entities/person.js';
import { isBeamterAbrechnungsgruppe } from '../entities/employee-sections/an-beschaeftigung.js';
import { type EmployeeDetailsUser, UserEmployeeDetailsActions } from '../UserEmployeeDetailsPolicy.js';
import { UserEmployeeDetailsSections } from './UserEmployeeDetailsSections.js';

export enum EmployeeTab {
  DETAILS = 'Details',
  VERTRAEGE = 'Verträge',
  LAK = 'LAK',
  BESCHAEFTIGUNGSGENEHMMIGUNG = 'Beschäftigungsgenehmigungen',
  TICKET = 'Ticket',
  KALENDER = 'Kalender',
}

const employeeSubPages = {
  details: {
    label: EmployeeTab.DETAILS,
    path: '/details',
    permission: UserEmployeeDetailsActions.VIEW_EMPLOYEE_SECTION_DETAILS,
  },
  vertraege: {
    label: EmployeeTab.VERTRAEGE,
    path: '/vertraege',
    permission: ContractsActions.VIEW_CONTRACTS,
  },
  lak: {
    label: EmployeeTab.LAK,
    path: '/lak',
    permission: UserEmployeeDetailsActions.VIEW_LAK,
    // decided on the server by the same rule the calculation follows (`keepsLAK`)
    attached: (viewedUser: EmployeeDetailsUser) => !!viewedUser.hasLAK,
  },
  beschaeftigungsgenehmigungen: {
    label: EmployeeTab.BESCHAEFTIGUNGSGENEHMMIGUNG,
    path: '/beschaeftigungsgenehmigungen',
    permission: UserEmployeeDetailsActions.VIEW_BESCHAEFTIGUNGSGENEHMIGUNG,
  },
  abwesenheiten: {
    label: EmployeeTab.KALENDER,
    path: '/kalender',
    permission: UserEmployeeDetailsActions.VIEW_KALENDER,
  },
  ticket: {
    label: EmployeeTab.TICKET,
    path: '/ticket',
    attached: (viewedUser: EmployeeDetailsUser) => {
      /**
       * Everyone who is not in this group is eligible for a ticket
       */
      return !isBeamterAbrechnungsgruppe(viewedUser.mitarbeiterDaten?.anBeschaeftigung?.abrechnungsgruppe);
    },
    permission: UserEmployeeDetailsActions.VIEW_TICKET_TAB,
  },
} as const satisfies Record<string, AbacEnabledPage<EmployeeDetailsUser>>;

export const employeeDetailRouter = makeRouter({
  root: { path: '/', render: () => nothing, documentTitlePart: '' },
  [employeeSubPages.details.label]: {
    path: employeeSubPages.details.path,
    render: params => UserEmployeeDetailsSections({ personID: params.personID }),
  },
  [employeeSubPages.vertraege.label]: {
    path: employeeSubPages.vertraege.path,
    render: params => ContractsView({ personID: params.personID }),
  },
  [employeeSubPages.lak.label]: {
    path: employeeSubPages.lak.path,
    render: params => LAKEmployeeOverview({ personID: params.personID }),
  },
  [employeeSubPages.abwesenheiten.label]: {
    path: employeeSubPages.abwesenheiten.path,
    render: params => SGCalender({ personID: params.personID }),
  },
  [employeeSubPages.ticket.label]: {
    path: employeeSubPages.ticket.path,
    render: params => MitarbeiterTicket({ personID: params.personID }),
  },
  [employeeSubPages.beschaeftigungsgenehmigungen.label]: {
    path: `${employeeSubPages.beschaeftigungsgenehmigungen.path}{/*}?`,
    subRouter: employmentPermitListRouter,
  },
});

export const SGEmployeeDetails = stateful((props: { personID: Maybe<string> }) => {
  if (!props.personID) throw new Error('No user given');

  const { value: person } = useQuery(
    () =>
      subscribeByID(
        Person,
        props.personID,
      )({
        _id: 1,
        roles: { name: 1, contexts: 1 },
        mitarbeiterDaten: { personalNr: 1, anBeschaeftigung: { abrechnungsgruppe: 1 } },
        sorgeberechtigteKinderIDs: 1,
        spendenKinderIDs: 1,
        hasLAK: 1,
      }),
    [props.personID],
  );

  const availablePages = useAbacEnabledPages<EmployeeDetailsUser>({
    pages: Object.values(employeeSubPages),
    viewedUser: person,
  });

  // TODO at the moment this works but it would be better if the router would handle this
  if (employeeDetailRouter.currentRoute.value.route.name === 'root') {
    employeeDetailRouter.goTo(employeeSubPages.details.label, { replaceState: true });
  }

  useEffect(() => {
    addPageHeaderAdditional(
      1,
      PageTabs({
        tabs: (availablePages ?? []).map(subPage => ({ key: subPage.label, label: subPage.label })),
        activeKey: () => employeeDetailRouter.currentRoute.value.route.name,
        onSelect: tabKey => {
          employeeDetailRouter.goTo(tabKey as (typeof employeeSubPages)[keyof typeof employeeSubPages]['label']);
        },
      }),
    );
    return () => {
      removePageHeaderAdditional(1);
    };
  }, [availablePages]);

  // like the person details: a pasted or reloaded URL reaches sections the tabs do not offer
  const currentPage = Object.values(employeeSubPages).find(
    page => page.label === employeeDetailRouter.currentRoute.value.route.name,
  );
  if (currentPage && !availablePages) return PageLoader({});
  if (currentPage && !availablePages?.includes(currentPage)) return NoPermission({});

  return html` ${employeeDetailRouter.outlet.value} `;
});
